import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type AnalyticsTier = "NONE" | "BASIC" | "ADVANCED";
export type BillingRole = "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER";
export type CustomerAccountType = "END_CUSTOMER" | "RESELLER";
export type BillingModel = "FIXED" | "PAYG";

export type StripeStatus = {
  required?: boolean;
  accountId?: string | null;
  connected?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  ready?: boolean;
  disabledReason?: string | null;
};

export type BillingContext = {
  role: BillingRole;
  accountType?: CustomerAccountType;
  billingModelVersion?: string;
  permissions: {
    manageMasterPlans: boolean;
    manageIntegratorPricing: boolean;
    createRetailPlans: boolean;
    assignRetailPlans: boolean;
  };
  stripe: StripeStatus;
  aiMonitoring?: {
    available: boolean;
    enabled: boolean;
    wholesaleInputPerMillionTokens: number;
    wholesaleOutputPerMillionTokens: number;
    retailInputPerMillionTokens: number;
    retailOutputPerMillionTokens: number;
    pricingBasis?: string;
  };
};

export type MasterPlan = {
  planId: string;
  billingModel?: BillingModel;
  name: string;
  retentionDays: number;
  analyticsTier: AnalyticsTier;
  resolution: { width: number; height: number };
  fps: number;
  bitrateKbps: number;
  codec: "H264" | string;
  currency?: string;
  defaultWholesaleMonthlyPrice?: number;
  wholesaleMonthlyPrice?: number;
  enabled?: boolean;
};

export type PlanConfiguration = {
  name?: string;
  billingModel?: BillingModel;
  retentionDays: number;
  analyticsTier: AnalyticsTier;
  resolution: { width: number; height: number };
  fps: number;
  bitrateKbps: number;
  codec: "H264" | string;
};

export type IntegratorBilling = {
  partnerId: string;
  displayName: string;
  email?: string | null;
  status?: string;
  stripe: StripeStatus;
  aiMonitoring: {
    enabled: boolean;
    wholesaleInputPerMillionTokens: number;
    wholesaleOutputPerMillionTokens: number;
    pricingBasis?: string;
  };
  entitlements: Array<{
    masterPlanId: string;
    enabled: boolean;
    wholesaleMonthlyPrice: number;
  }>;
};

export type RetailPlan = {
  retailPlanId: string;
  billingModel?: BillingModel;
  masterPlanId?: string;
  name: string;
  retailMonthlyPrice: number;
  wholesaleMonthlyPrice?: number;
  currency: string;
  enabled: boolean;
  configuration: PlanConfiguration;
};

export type BillingCustomer = {
  customerId: string;
  displayName: string;
  email?: string | null;
  accountType: CustomerAccountType;
  assignedCameraIds: string[];
  stripe: StripeStatus;
};

export type CustomerBillingItem = {
  cameraId: string;
  cameraName?: string | null;
  planName?: string | null;
  monthlyPrice: number;
  retentionDays: number;
  analyticsTier: AnalyticsTier;
  aiMonitoring: {
    enabled: boolean;
    retailInputPerMillionTokens: number;
    retailOutputPerMillionTokens: number;
  };
};

async function token(): Promise<string> {
  const session = await fetchAuthSession();
  const value = session.tokens?.idToken?.toString();
  if (!value) throw new Error("Your session has expired. Sign out and sign in again.");
  return value;
}

async function readResponse(response: Response) {
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const error = new Error(
      payload?.message ||
        payload?.error ||
        text ||
        `Billing request failed (${response.status})`,
    );
    (error as any).code = payload?.code;
    throw error;
  }
  return payload;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const idToken = await token();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });
  return readResponse(response) as Promise<T>;
}

export function getBillingContext() {
  return api<BillingContext>("/billing/context");
}

export async function listMasterPlans(): Promise<MasterPlan[]> {
  const payload = await api<{ items: MasterPlan[] }>("/billing/master-plans");
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function createMasterPlan(
  input: Omit<MasterPlan, "planId" | "codec">,
): Promise<MasterPlan> {
  const payload = await api<{ plan: MasterPlan }>("/billing/master-plans", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return payload.plan;
}

export async function updateMasterPlan(
  planId: string,
  input: Partial<MasterPlan>,
): Promise<MasterPlan> {
  const payload = await api<{ plan: MasterPlan }>(
    `/billing/master-plans/${encodeURIComponent(planId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return payload.plan;
}

export async function listIntegrators(): Promise<IntegratorBilling[]> {
  const payload = await api<{ items: IntegratorBilling[] }>(
    "/billing/integrators",
  );
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function saveIntegratorEntitlements(
  partnerId: string,
  input: {
    plans: Array<{
      masterPlanId: string;
      enabled: boolean;
      wholesaleMonthlyPrice: number;
    }>;
    aiMonitoring: {
      enabled: boolean;
      wholesaleInputPerMillionTokens: number;
      wholesaleOutputPerMillionTokens: number;
    };
  },
): Promise<IntegratorBilling> {
  const payload = await api<{ integrator: IntegratorBilling }>(
    `/billing/integrators/${encodeURIComponent(partnerId)}/entitlements`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return payload.integrator;
}

export async function listRetailPlans(): Promise<RetailPlan[]> {
  const payload = await api<{ items: RetailPlan[] }>("/billing/retail-plans");
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function createRetailPlan(input: {
  masterPlanId: string;
  name: string;
  retailMonthlyPrice: number;
}): Promise<RetailPlan> {
  const payload = await api<{ plan: RetailPlan }>("/billing/retail-plans", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return payload.plan;
}

export async function updateRetailPlan(
  retailPlanId: string,
  input: Partial<
    Pick<RetailPlan, "name" | "retailMonthlyPrice" | "enabled">
  > & { masterPlanId?: string },
): Promise<RetailPlan> {
  const payload = await api<{ plan: RetailPlan }>(
    `/billing/retail-plans/${encodeURIComponent(retailPlanId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return payload.plan;
}

export async function saveAiMonitoringRetailPricing(input: {
  enabled: boolean;
  retailInputPerMillionTokens: number;
  retailOutputPerMillionTokens: number;
}) {
  const payload = await api<{
    aiMonitoring: {
      enabled: boolean;
      retailInputPerMillionTokens: number;
      retailOutputPerMillionTokens: number;
    };
  }>("/billing/ai-monitoring", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return payload.aiMonitoring;
}

export async function listBillingCustomers(): Promise<BillingCustomer[]> {
  const payload = await api<{ items: BillingCustomer[] }>("/billing/customers");
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function setCustomerAccountType(
  customerId: string,
  accountType: CustomerAccountType,
) {
  const payload = await api<{ customer: BillingCustomer }>(
    `/billing/customers/${encodeURIComponent(customerId)}/account-type`,
    { method: "PUT", body: JSON.stringify({ accountType }) },
  );
  return payload.customer;
}

export async function connectStripe() {
  return api<{ url: string; expiresAt?: number | null; stripe: StripeStatus }>(
    "/billing/stripe/connect",
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function refreshStripeStatus() {
  return api<{ stripe: StripeStatus }>("/billing/stripe/status");
}

export async function connectCustomerStripe(customerId: string) {
  return api<{
    customerId: string;
    url: string;
    expiresAt?: number | null;
    stripe: StripeStatus;
  }>(`/billing/customers/${encodeURIComponent(customerId)}/stripe/connect`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

function chooseMainH264Profile(profiles: any[]) {
  const candidates = (Array.isArray(profiles) ? profiles : []).filter(
    (profile) =>
      String(profile?.encoding || "").trim().toUpperCase() === "H264" &&
      profile?.profileSelectable !== false,
  );
  const score = (profile: any) => {
    const label = `${profile?.profileName || ""} ${profile?.profileToken || ""}`
      .toLowerCase()
      .replace(/[_-]+/g, " ");
    const named = /\bmain\s*stream\b/.test(label)
      ? 3
      : /\bmain\b/.test(label)
        ? 2
        : /\bprofile\s*1\b/.test(label)
          ? 1
          : 0;
    const pixels =
      Number(profile?.current?.resolution?.width || 0) *
      Number(profile?.current?.resolution?.height || 0);
    return { named, pixels };
  };
  candidates.sort(
    (left, right) =>
      score(right).named - score(left).named ||
      score(right).pixels - score(left).pixels,
  );
  if (!candidates.length) {
    throw new Error(
      "This camera does not expose a selectable H.264 main stream required by the selected plan.",
    );
  }
  return candidates[0];
}

async function applyCameraProfile(
  cameraId: string,
  configuration: PlanConfiguration,
) {
  const idToken = await token();
  const url = `${API_BASE}/cameras/${encodeURIComponent(cameraId)}/video-settings`;
  const discoveryResponse = await fetch(url, {
    headers: { Accept: "application/json", Authorization: `Bearer ${idToken}` },
  });
  const discovery = await readResponse(discoveryResponse);
  const profile = chooseMainH264Profile(discovery?.profiles);
  if (!profile?.profileToken) {
    throw new Error(
      "The selected camera main stream has no ONVIF profile token.",
    );
  }

  const applyResponse = await fetch(url, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      profileToken: profile.profileToken,
      settings: {
        resolution: configuration.resolution,
        fps: configuration.fps,
        bitrateKbps: configuration.bitrateKbps,
      },
    }),
  });
  await readResponse(applyResponse);
}

export async function assignRetailPlan(input: {
  customerId: string;
  cameraId: string;
  plan: RetailPlan;
  aiMonitoringEnabled?: boolean;
}) {
  await applyCameraProfile(input.cameraId, input.plan.configuration);
  return api<{
    camera: any;
    plan: RetailPlan;
    aiMonitoring: {
      enabled: boolean;
      retailInputPerMillionTokens: number;
      retailOutputPerMillionTokens: number;
    };
    retentionSync: any;
  }>("/billing/assignments", {
    method: "POST",
    body: JSON.stringify({
      customerId: input.customerId,
      cameraId: input.cameraId,
      retailPlanId: input.plan.retailPlanId,
      aiMonitoringEnabled: input.aiMonitoringEnabled === true,
    }),
  });
}

export function createCustomerCheckout(customerId: string, cameraId: string) {
  return api<{ url: string; sessionId: string }>("/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ customerId, cameraId }),
  });
}

export async function getCustomerBillingView(): Promise<CustomerBillingItem[]> {
  const payload = await api<{ items: CustomerBillingItem[] }>(
    "/billing/customer-view",
  );
  return Array.isArray(payload.items) ? payload.items : [];
}
